/**
 * Phase 7 — Quick Request Sheet.
 *
 * Right-side drawer (desktop) / full-screen sheet (mobile) launched from
 * the Package Detail page. Skips the full /plan 6-step builder for
 * high-intent buyers who already picked a specific package.
 *
 * Single-overlay design:
 *   step="form"        — collect inputs
 *   step="otp"         — verify phone inline (NOT a second drawer)
 *   step="submitting"  — brief "Sending your request…" spinner
 *
 * Flow:
 *   1) Validate form, scroll to first invalid field.
 *   2) POST /api/quote-drafts (fresh, ephemeral — NOT persisted in
 *      localStorage so ResumeBar stays untouched for quick-request flows).
 *   3) POST /api/auth/otp/start → switch the SAME drawer body to the OTP view.
 *   4) Verify code in-place. On success → POST /api/quotes → navigate
 *      /quotes/{id}/success and close the drawer.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "../ui/sonner";

import { upsertDraft, submitQuote, startOtp, verifyOtp, persistAuth } from "../../lib/api";
import { OtpBoxes } from "../auth/OtpSheet";
import {
  CITIES,
  COUNTRY_CODES,
  BEST_TIME_OPTIONS,
  GUEST_BANDS,
} from "./quick-request-constants";
import { formatINR } from "../admin/admin-ui";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;

const DATE_MODES = [
  { key: "exact", label: "I have an exact date" },
  { key: "flexible_month", label: "Flexible — pick a month" },
  { key: "exploring", label: "Not sure yet" },
];

const SERVICE_SLUG_GUEST_REQUIRED = new Set([
  "wedding-photography",
  "events-celebrations",
]);

export default function QuickRequestSheet({ open, onClose, pkg, service }) {
  const navigate = useNavigate();
  const guestRequired = useMemo(
    () => SERVICE_SLUG_GUEST_REQUIRED.has(service?.slug),
    [service?.slug],
  );

  // Form state
  const [dateMode, setDateMode] = useState("exact");
  const [eventDate, setEventDate] = useState("");
  const [flexibleMonth, setFlexibleMonth] = useState("");
  const [city, setCity] = useState("");
  const [venue, setVenue] = useState("");
  const [guestBand, setGuestBand] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [bestTime, setBestTime] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // OTP / step state
  const [step, setStep] = useState("form"); // "form" | "otp" | "submitting"
  const [devOtp, setDevOtp] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const firstOtpRef = useRef(null);

  // Field refs for scroll-to-first-invalid. Declared individually so the
  // eslint `react-hooks/rules-of-hooks` checker can verify ordering, then
  // assembled into a regular object below.
  const refEventDate = useRef(null);
  const refFlexibleMonth = useRef(null);
  const refCity = useRef(null);
  const refGuestBand = useRef(null);
  const refName = useRef(null);
  const refEmail = useRef(null);
  const refPhone = useRef(null);
  const refBestTime = useRef(null);
  const fieldRefs = {
    eventDate: refEventDate,
    flexibleMonth: refFlexibleMonth,
    city: refCity,
    guestBand: refGuestBand,
    name: refName,
    email: refEmail,
    phone: refPhone,
    bestTime: refBestTime,
  };

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Countdown for the inline OTP view's resend timer.
  useEffect(() => {
    if (!open || step !== "otp" || resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, step, resendIn]);

  // Auto-focus the first OTP box when entering the OTP step.
  useEffect(() => {
    if (!open || step !== "otp") return undefined;
    const t = setTimeout(() => firstOtpRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open, step]);

  if (!open) return null;

  function validate() {
    const e = {};
    if (dateMode === "exact" && !eventDate) e.eventDate = "Pick a date.";
    if (dateMode === "flexible_month" && !flexibleMonth) e.flexibleMonth = "Pick a month.";
    if (!city) e.city = "Pick a city.";
    if (guestRequired && !guestBand) e.guestBand = "Pick a guest count.";
    if (!name.trim()) e.name = "Add your name.";
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) e.email = "Enter a valid email.";
    if (!phone.trim()) e.phone = "Enter your phone.";
    else if (countryCode === "+91" && !/^\d{10}$/.test(phone.trim())) e.phone = "Enter 10 digits.";
    if (!bestTime) e.bestTime = "Pick the best time to reach you.";
    setErrors(e);
    if (Object.keys(e).length) {
      const first = Object.keys(e)[0];
      const ref = fieldRefs[first];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof ref.current.focus === "function") ref.current.focus({ preventScroll: true });
      }
      return false;
    }
    return true;
  }

  function buildPayload() {
    return {
      source_package_id: pkg.id,
      quick_request: true,
      // occasion is inferred server-side from the package's parent service
      date_mode: dateMode,
      event_date: dateMode === "exact" ? eventDate : null,
      flexible_month: dateMode === "flexible_month" ? flexibleMonth : null,
      locations: [
        {
          city,
          venue_name: venue || null,
          country: "India",
          mode: "both",
        },
      ],
      scope: guestRequired ? { guest_count_band: guestBand } : {},
      contact: {
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        country_code: countryCode,
        best_time_to_reach: bestTime,
        notes: notes.trim() || null,
      },
    };
  }

  async function onSend() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // 1) Create ephemeral draft (do NOT touch localStorage)
      const payload = buildPayload();
      const draft = await upsertDraft(null, payload);
      const newDraftId = draft.draft_id || draft.id;
      setDraftId(newDraftId);

      // 2) Start OTP, then switch the SAME drawer body to the OTP view.
      const otp = await startOtp({ phone: phone.trim(), country_code: countryCode });
      setDevOtp(otp?.dev_otp || null);
      setOtpCode("");
      setOtpError(null);
      setResendIn(RESEND_COOLDOWN_S);
      setStep("otp");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Couldn't send your request. Please check the form and retry."));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyAndSubmit() {
    if (otpCode.length !== OTP_LENGTH || !/^\d{6}$/.test(otpCode)) {
      setOtpError("Enter the 6-digit code.");
      return;
    }
    setVerifying(true);
    setOtpError(null);
    try {
      // 1) Verify the code (sets buyer tokens on success)
      const auth = await verifyOtp({
        phone: phone.trim(),
        code: otpCode,
        country_code: countryCode,
      });
      persistAuth(auth);

      // 2) Submit the quote — switch the drawer to a brief "submitting" view
      //    so the user sees progress (not a frozen-looking OTP screen).
      setStep("submitting");
      const q = await submitQuote(draftId);
      const quoteId = typeof q?.id === "string" ? q.id : String(q?.id || "");
      if (!quoteId) throw new Error("Quote was created but no id was returned.");
      toast.success("Request sent!");
      // Close the drawer first, then navigate — keeps focus management clean.
      onClose?.();
      navigate(`/quotes/${quoteId}/success`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Verify-step errors stay INSIDE the OTP view so the user can retry.
      setStep("otp");
      setOtpError(
        typeof detail === "string"
          ? detail
          : detail?.message ||
              (status === 429
                ? "Too many attempts. Try later."
                : "Couldn't verify. Try again."),
      );
    } finally {
      setVerifying(false);
    }
  }

  async function resendOtp() {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setOtpError(null);
    try {
      const otp = await startOtp({ phone: phone.trim(), country_code: countryCode });
      setDevOtp(otp?.dev_otp || null);
      setOtpCode("");
      setResendIn(RESEND_COOLDOWN_S);
      toast.success("New code sent.");
    } catch (e) {
      setOtpError(extractErrorMessage(e, "Couldn't resend. Try again."));
    } finally {
      setResending(false);
    }
  }

  const titleId = "quick-request-title";

  return (
    <div
      data-testid="quick-request-sheet"
      className="fixed inset-0 z-[70] flex bg-[#1A1A1A]/55"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="ml-auto flex h-full w-full flex-col bg-[#FAF7F2] shadow-2xl sm:max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-[#1A1A1A]/8 bg-[#FAF7F2]/95 px-5 py-4 backdrop-blur sm:px-6">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
              ShutterKART
            </p>
            <h2 id={titleId} className="mt-0.5 font-display text-2xl text-[#1A1A1A]">
              Request a quote
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-request-close"
            aria-label="Close"
            className="rounded-full p-2 hover:bg-[#1A1A1A]/5"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Scrollable body */}
        {step === "form" ? (
        <>
        <div className="flex-1 overflow-y-auto px-5 pb-32 pt-4 sm:px-6">
          {/* Package preview */}
          <div
            data-testid="quick-request-pkg-preview"
            className="flex items-center gap-3 rounded-2xl border border-[#1A1A1A]/10 bg-white p-3"
          >
            {pkg.hero_image_url ? (
              <img
                src={pkg.hero_image_url}
                alt={pkg.name}
                className="h-16 w-16 rounded-xl object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-[#1A1A1A] to-[#C8A24B]" />
            )}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Selected package
              </p>
              <p className="truncate font-display text-base text-[#1A1A1A]">{pkg.name}</p>
              {pkg.base_price_inr ? (
                <p className="text-xs text-[#1A1A1A]/65">
                  starting {formatINR(pkg.base_price_inr)}
                </p>
              ) : null}
            </div>
          </div>

          {/* 1) Event date */}
          <Section title="Event date">
            <ChipRow>
              {DATE_MODES.map((m) => (
                <Chip
                  key={m.key}
                  testid={`date-mode-${m.key}`}
                  active={dateMode === m.key}
                  onClick={() => setDateMode(m.key)}
                >
                  {m.label}
                </Chip>
              ))}
            </ChipRow>
            {dateMode === "exact" ? (
              <Field error={errors.eventDate}>
                <input
                  ref={fieldRefs.eventDate}
                  data-testid="quick-event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="mt-2 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
                />
              </Field>
            ) : null}
            {dateMode === "flexible_month" ? (
              <Field error={errors.flexibleMonth}>
                <input
                  ref={fieldRefs.flexibleMonth}
                  data-testid="quick-flexible-month"
                  type="month"
                  value={flexibleMonth}
                  onChange={(e) => setFlexibleMonth(e.target.value)}
                  className="mt-2 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
                />
              </Field>
            ) : null}
          </Section>

          {/* 2) City + venue */}
          <Section title="Where">
            <Field label="City" error={errors.city}>
              <select
                ref={fieldRefs.city}
                data-testid="quick-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
              >
                <option value="">Pick a city</option>
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Venue / location (optional)">
              <input
                data-testid="quick-venue"
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="e.g. The Leela Palace, Bandstand"
                className="mt-1 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
              />
            </Field>
          </Section>

          {/* 3) Guest count — only for wedding/events */}
          {guestRequired ? (
            <Section title="Number of guests">
              <Field error={errors.guestBand}>
                <ChipRow>
                  {GUEST_BANDS.map((g) => (
                    <Chip
                      key={g.key}
                      testid={`quick-guest-${g.key}`}
                      active={guestBand === g.key}
                      onClick={() => setGuestBand(g.key)}
                    >
                      {g.label}
                    </Chip>
                  ))}
                </ChipRow>
                <button
                  ref={fieldRefs.guestBand}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
              </Field>
            </Section>
          ) : null}

          {/* 4) Contact details */}
          <Section title="Your contact details">
            <Field label="Full name" error={errors.name}>
              <input
                ref={fieldRefs.name}
                data-testid="quick-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="mt-1 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
              />
            </Field>
            <Field label="Email" error={errors.email}>
              <input
                ref={fieldRefs.email}
                data-testid="quick-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1 w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
              />
            </Field>
            <Field label="Phone" error={errors.phone}>
              <div className="mt-1 flex gap-2">
                <select
                  data-testid="quick-country-code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="w-28 rounded-md border border-[#1A1A1A]/15 bg-white px-2 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} {c.flag}
                    </option>
                  ))}
                </select>
                <input
                  ref={fieldRefs.phone}
                  data-testid="quick-phone"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
                  autoComplete="tel-national"
                  placeholder={countryCode === "+91" ? "10 digits" : "phone"}
                  className="flex-1 rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
                />
              </div>
            </Field>
            <Field label="Best time to reach" error={errors.bestTime}>
              <ChipRow>
                {BEST_TIME_OPTIONS.map((o) => (
                  <Chip
                    key={o.key}
                    testid={`quick-time-${o.key}`}
                    active={bestTime === o.key}
                    onClick={() => setBestTime(o.key)}
                  >
                    {o.label}
                  </Chip>
                ))}
              </ChipRow>
              <button
                ref={fieldRefs.bestTime}
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
              />
            </Field>
          </Section>

          {/* 5) Notes */}
          <Section title="Anything else?">
            <div className="relative">
              <textarea
                data-testid="quick-notes"
                value={notes}
                onChange={(e) =>
                  setNotes(e.target.value.length <= 500 ? e.target.value : e.target.value.slice(0, 500))
                }
                rows={4}
                placeholder="Share any specifics — vibe, must-have shots, languages spoken at the event, etc."
                className="w-full rounded-md border border-[#1A1A1A]/15 bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
              />
              <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45">
                {notes.length}/500
              </p>
            </div>
          </Section>
        </div>

        {/* Sticky submit footer */}
        <footer className="sticky bottom-0 border-t border-[#1A1A1A]/8 bg-[#FAF7F2] px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 sm:px-6">
          <button
            type="button"
            data-testid="quick-request-send"
            onClick={onSend}
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#C8A24B] px-5 py-3 text-[11px] uppercase tracking-[0.24em] text-[#1A1A1A] transition hover:bg-[#b18d3a] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {submitting ? "Sending…" : "Send this request"}
            {!submitting ? <ChevronRight className="h-3.5 w-3.5" /> : null}
          </button>
          <p className="mt-2 text-center text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/50">
            We typically respond within 24 business hours.
          </p>
        </footer>
        </>
        ) : null}

        {/* ---------- OTP view (inline, same drawer) ---------- */}
        {step === "otp" ? (
          <>
          <div
            data-testid="quick-request-otp-view"
            className="flex-1 overflow-y-auto px-5 pb-32 pt-4 sm:px-6"
          >
            <button
              type="button"
              data-testid="quick-otp-back"
              onClick={() => {
                setOtpError(null);
                setStep("form");
              }}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/65 hover:text-[#1A1A1A]"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to details
            </button>

            <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
              Verify your phone
            </p>
            <h3 className="mt-1 font-display text-2xl text-[#1A1A1A]">
              Enter the 6-digit code
            </h3>
            <p className="mt-2 text-sm text-[#1A1A1A]/65">
              We sent it to{" "}
              <span className="font-medium text-[#1A1A1A]">
                {formatPhoneMask(countryCode, phone.trim())}
              </span>
              .{" "}
              <button
                type="button"
                data-testid="quick-otp-edit-phone"
                onClick={() => {
                  setOtpError(null);
                  setStep("form");
                  setTimeout(() => fieldRefs.phone.current?.focus(), 80);
                }}
                className="underline decoration-[#C8A24B] underline-offset-4 hover:text-[#1A1A1A]"
              >
                Edit number
              </button>
            </p>

            <OtpBoxes
              value={otpCode}
              onChange={setOtpCode}
              firstInputRef={firstOtpRef}
              disabled={verifying}
              onComplete={onVerifyAndSubmit}
            />

            {devOtp ? (
              <p
                data-testid="otp-dev-hint"
                className="mt-3 text-center text-[11px] text-[#1A1A1A]/55"
              >
                <span className="rounded-full bg-[#1A1A1A]/5 px-2 py-1 font-mono tracking-widest text-[#1A1A1A]/75">
                  Dev OTP: {devOtp}
                </span>{" "}
                <span className="ml-1">(REAL_SMS_ENABLED=false)</span>
              </p>
            ) : null}

            {otpError ? (
              <p
                data-testid="quick-otp-error"
                role="alert"
                className="mt-3 text-center text-xs text-[#B0413E]"
              >
                {otpError}
              </p>
            ) : null}

            <div className="mt-5 text-center text-xs text-[#1A1A1A]/55">
              {resendIn > 0 ? (
                <span data-testid="quick-otp-resend-countdown">
                  Resend in{" "}
                  <span className="font-medium text-[#1A1A1A]">{resendIn}s</span>
                </span>
              ) : (
                <button
                  type="button"
                  data-testid="quick-otp-resend"
                  disabled={resending}
                  onClick={resendOtp}
                  className="font-medium text-[#1A1A1A] underline decoration-[#C8A24B] underline-offset-4 hover:text-[#C8A24B]"
                >
                  {resending ? "Sending…" : "Resend code"}
                </button>
              )}
            </div>
          </div>

          <footer className="sticky bottom-0 border-t border-[#1A1A1A]/8 bg-[#FAF7F2] px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 sm:px-6">
            <button
              type="button"
              data-testid="quick-otp-verify"
              onClick={onVerifyAndSubmit}
              disabled={verifying || otpCode.length !== OTP_LENGTH}
              className={[
                "inline-flex w-full items-center justify-center gap-1.5 rounded-full px-5 py-3 text-[11px] uppercase tracking-[0.24em] transition",
                verifying || otpCode.length !== OTP_LENGTH
                  ? "cursor-not-allowed bg-[#C8A24B]/40 text-[#1A1A1A]/55"
                  : "bg-[#C8A24B] text-[#1A1A1A] hover:bg-[#b18d3a]",
              ].join(" ")}
            >
              {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {verifying ? "Verifying…" : "Verify & send"}
            </button>
          </footer>
          </>
        ) : null}

        {/* ---------- Submitting view (brief spinner) ---------- */}
        {step === "submitting" ? (
          <div
            data-testid="quick-request-submitting"
            className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center"
          >
            <Loader2 className="h-6 w-6 animate-spin text-[#C8A24B]" />
            <p className="font-display text-lg text-[#1A1A1A]">
              Sending your request…
            </p>
            <p className="text-xs text-[#1A1A1A]/55">
              One moment — wrapping up.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Mask the middle digits of the phone number for the OTP step's "code sent
// to +91 9XXXX XX210" label. Defensive against short numbers.
function formatPhoneMask(countryCode, phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length < 4) return `${countryCode} ${digits || "—"}`;
  const last3 = digits.slice(-3);
  const masked = digits.slice(0, -3).replace(/\d/g, "X");
  // Insert a space halfway through the masked portion for readability.
  const mid = Math.ceil(masked.length / 2);
  const pretty = `${masked.slice(0, mid)} ${masked.slice(mid)}`.trim();
  return `${countryCode} ${pretty} ${last3}`;
}

// FastAPI 422 returns `{detail: [{loc, msg, type}]}` where detail is an
// ARRAY OF OBJECTS. Passing that array directly to <toast.error> (or any JSX
// child) throws "Objects are not valid as a React child". Always coerce to a
// human-readable string here.
function extractErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && (d.msg || d.message));
    if (first) return first.msg || first.message;
  }
  if (detail && typeof detail === "object") {
    return detail.message || fallback;
  }
  if (err?.message && typeof err.message === "string") return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------
function Section({ title, children }) {
  return (
    <section className="mt-5">
      <p className="mb-2 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
        {title}
      </p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Field({ label, error, children }) {
  return (
    <div>
      {label ? (
        <label className="text-xs text-[#1A1A1A]/75">{label}</label>
      ) : null}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-[#B0413E]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
function ChipRow({ children }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}
function Chip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={[
        "rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] transition",
        active
          ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
          : "border-[#1A1A1A]/15 bg-white text-[#1A1A1A]/75 hover:border-[#C8A24B] hover:text-[#1A1A1A]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
